'use client';
import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Button from '../ui/Button';
import HeaderMenu from './HeaderMenu';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsSticky(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll
  const handleSmoothScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    const [path, hash] = href.split('#');

    if (hash) {
      if (window.location.pathname === '/' || path === '') {
        e.preventDefault();
        const el = document.getElementById(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.location.href = `/#${hash}`;
        }
        setIsMobileMenuOpen(false);
      } else {
        e.preventDefault();
        window.location.href = `/#${hash}`;
      }
    }
  };

  return (
    <header
      className={`w-full z-50 transition-all duration-300 ${
        isSticky ? 'fixed top-0 left-0 shadow-lg bg-white' : 'absolute top-0 left-0 bg-transparent'
      }`}
    >
      <div className="w-full max-w-[1340px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 md:py-6">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/images/site-logo.webp"
              alt="Logo"
              width={158}
              height={64}
              unoptimized
              className="w-[120px] h-[48px] sm:w-[140px] sm:h-[56px] md:w-[158px] md:h-[64px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <HeaderMenu onLinkClick={handleSmoothScroll} />
          </nav>

          {/* Desktop Button */}
          <div className="hidden lg:block">
            <Link href="/contact">
              <Button
                text="Contact Us"
                text_font_size="text-base"
                text_font_family="HelveticaNeue"
                text_font_weight="medium"
                text_line_height="leading-xl"
                text_text_align="center"
                text_color="text-white"
                fill_background_color="bg-[#FA4B80]"
                border_border_radius="rounded-sm"
                layout_align_self="center"
                className="gap-[2px] py-[14px] px-[22px] pr-[36px] hover:bg-black transition-colors duration-200"
              >
                <span className="text-white">Contact </span>
                <span className="text-[#fff]">Us</span>
                <Image
                  src="/images/img-line-rounded-arrow-rigth.webp"
                  alt="Arrow"
                  width={14}
                  height={14}
                  className="ml-1"
                  unoptimized
                />
              </Button>
            </Link>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden p-2"
            aria-label="Toggle menu"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center">
              <span
                className={`block w-6 h-0.5 bg-black transition-all duration-300 ${
                  isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-black mt-1 transition-all duration-300 ${
                  isMobileMenuOpen ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`block w-6 h-0.5 bg-black mt-1 transition-all duration-300 ${
                  isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <nav className="lg:hidden absolute top-full left-0 w-full bg-white z-50 shadow-lg border-t border-gray-200">
          <div className="flex flex-col p-4 space-y-4">
            <HeaderMenu onLinkClick={handleSmoothScroll} />
            <Link href="/contact">
              <Button
                text="Contact Us"
                text_font_size="text-base"
                text_font_family="Inter"
                text_font_weight="font-semibold"
                text_line_height="leading-xl"
                text_text_align="center"
                text_color="text-white"
                fill_background_color="bg-[#FA4B80]"
                border_border_radius="rounded-sm"
                layout_align_self="center"
                className="w-full gap-[2px]  py-[14px] px-[22px] pr-[36px]hover:bg-black transition-colors duration-200"
              >
                <span className="text-white">Contact </span>
                <span className="text-[#fa4b7f]">Us</span>
                <Image
                  src="/images/img-line-rounded-arrow-rigth.webp"
                  alt="Arrow"
                  width={14}
                  height={14}
                  className="ml-1"
                  unoptimized
                />
              </Button>
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;